import _ from "lodash";
import { C8Node, C8NodeType } from "src/c8-platform/type/node";
import { default as ExtendedPath } from "csi/core/ExtendedPath";
import { parseExtendedPath } from "./extendedPathService";

/**
 * Filter out all paths that are superseded by (longer than) this path from the same start type;
 * don't search for shorter paths on other types as clearing those will cause paths to be lost if the
 * user wants to change their decision and undo the change
 */
function _updatePaths(pathsPerType: ExtendedPath[], ep: ExtendedPath, removedPaths?: string[]): ExtendedPath[] {
	return pathsPerType.filter((existingPath) => {
		if (existingPath.isAlongPath(ep)) {
			removedPaths?.push(existingPath.toString());
			return false;
		}

		return true;
	});
}

class PathHints {
	/** name identifying path hints */
	readonly name?: string;

	/** optional hints/hidden path to act as defaults if hint/hidden path not located on this object */
	readonly #defaultPathHints: PathHints;

	/** object holding extended paths per start type and path for fast lookup */
	readonly #hints: Record<C8NodeType, Record<string, ExtendedPath>> = {};

	/** object holding hidden paths per start type for fast lookup */
	readonly #hiddenPaths: Record<C8NodeType, ExtendedPath[]> = {};

	/** hints to be applied globally */
	static readonly globalHints = new PathHints();

	constructor(defaultPathHints?: PathHints, name?: string) {
		this.name = name;
		this.#defaultPathHints = defaultPathHints;

		// assign global hints as defaults - except for the globalHints instance itself
		if (!defaultPathHints && (PathHints.globalHints !== this)) {
			this.#defaultPathHints = PathHints.globalHints;
		}
	}

	/**
	 * Register path hint with system
	 */
	add(ep: ExtendedPath): void {
		const startType = ep.getStartType();
		if (!startType) {
			throw new Error("PathHints requires typed path");
		}

		const obj = this.#hints[startType] ||= {};

		// clear any indexes in paths as they do not affect classes long path
		ep = ep.clearStepKeys();

		obj[ep.getStandardPath()] = ep;
	}

	/**
	 * Lookup typed path hint given starting type and standard path
	 */
	get(startType: C8NodeType, path: string): ExtendedPath {
		return this.#hints[startType]?.[path] || this.#defaultPathHints?.get(startType, path);
	}

	/**
	 * return all paths
	 */
	get hintPaths(): ExtendedPath[] {
		return _.flatten(Object.values(this.#hints).map(Object.values));
	}

	/**
	 * Lookup target type for path starting at given type
	 */
	getTarget(startType: C8NodeType, path: string): C8NodeType {
		return this.get(startType, path)?.getEndType();
	}

	/**
	 * Add hidden path, and remove any paths that were further along the given path from the same start type
	 */
	addHiddenPath(ep: ExtendedPath, removedPaths?: string[]): void {
		const type = ep.getStartType();

		this.#hiddenPaths[type] = _updatePaths(this.#hiddenPaths[type] || [], ep, removedPaths);
		this.#hiddenPaths[type].push(ep);

		// if starting class is abstract, then also add this path to any derived classes
		ep.startClass?.derivedClasses.forEach(({ type }) => {
			this.#hiddenPaths[type] = _updatePaths(this.#hiddenPaths[type] || [], ep, removedPaths);
			this.#hiddenPaths[type].push(ep);
		});
	}

	/**
	 * Add hidden paths from SitePreferences node to set of configured paths.
	 */
	addHiddenPathsFromPreferences(sitePreferences: C8Node): void {
		sitePreferences.HiddenPaths?.forEach((p: string) => {
			try {
				this.addHiddenPath(parseExtendedPath(p));
			} catch (ex) {
				// ignore bad paths
			}
		});
	}

	/**
	 * Determine if given path is hidden directly, or indirectly by containing some subpath that is hidden on another type.
	 */
	isPathHidden(ep: ExtendedPath, bSkipStart?: boolean): boolean {
		ep = ep.clone();

		if (bSkipStart) {
			ep.shiftDomain(true);
		}

		if (ep.depth) {
			while (ep.depth) {
				ep.startFromDomainType();

				if (this.isPathHiddenFromStartType(ep)) {
					return true;
				}

				ep.shiftDomain(true);
			}

			return false;
		}
        
		return this.isPathHiddenFromStartType(ep);
	}

	/**
	 * Determine if given path is hidden from the starting type. It could be a hidden path exactly, or an extension of an existing hidden path.
	 */
	isPathHiddenFromStartType(ep: ExtendedPath): boolean {
		const type = ep.getStartType();
		const b = this.#hiddenPaths[type]?.some((epHidden) => ep.isAlongPath(epHidden)) || this.#defaultPathHints?.isPathHiddenFromStartType(ep);
		return !!b;
	}

	/**
	 * Remove given hidden path and any other paths that are along that path.
	 */
	removeHiddenPath(ep: ExtendedPath, removedPaths?: string[]): void {
		const type = ep.getStartType();

		if (this.#hiddenPaths[type]) {
			this.#hiddenPaths[type] = _updatePaths(this.#hiddenPaths[type] || [], ep, removedPaths);
		}

		// if starting class is abstract, then also remove this path from any derived classes
		ep.startClass?.derivedClasses.forEach(({ type }) => {
			this.#hiddenPaths[type] = _updatePaths(this.#hiddenPaths[type] || [], ep, removedPaths);
		});
	}
}

export = PathHints;